import { dayKey } from "./format";
import { byDay, closedOnly, equityCurve, net, summarize, type Summary } from "./stats";
import type { LiteTrade } from "./types";

// Analytics avancées du Lite, calculs purs. Les fonctions renvoient des clés ;
// les libellés sont traduits dans l'interface.

const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
const std = (xs: number[]) => {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  return Math.sqrt(xs.reduce((a, x) => a + (x - m) ** 2, 0) / (xs.length - 1));
};

export interface RiskMetrics {
  sharpe: number;
  sortino: number;
  recoveryFactor: number;
  maxDrawdown: number;
  avgDaily: number;
  dailyStd: number;
  /** Part du meilleur jour dans le profit total (règle de constance des prop firms). */
  consistency: number;
  bestDay: number;
  worstDay: number;
  winDays: number;
  lossDays: number;
  /** Perte journalière dépassée 1 jour sur 20. */
  var95: number;
}

export function riskMetrics(trades: LiteTrade[]): RiskMetrics {
  const days = byDay(closedOnly(trades));
  const daily = days.map(d => d.pnl);
  const m = mean(daily);
  const sd = std(daily);
  const downside = std(daily.map(x => Math.min(0, x)));
  const maxDd = equityCurve(trades, "en-US").reduce((a, p) => Math.min(a, p.drawdown), 0);
  const total = daily.reduce((a, b) => a + b, 0);
  const best = Math.max(0, ...daily);
  const sorted = [...daily].sort((a, b) => a - b);
  return {
    sharpe: sd ? (m / sd) * Math.sqrt(252) : 0,
    sortino: downside ? (m / downside) * Math.sqrt(252) : 0,
    recoveryFactor: maxDd ? total / Math.abs(maxDd) : 0,
    maxDrawdown: maxDd,
    avgDaily: m,
    dailyStd: sd,
    consistency: total > 0 ? best / total : 0,
    bestDay: best,
    worstDay: Math.min(0, ...daily),
    winDays: daily.filter(x => x > 0).length,
    lossDays: daily.filter(x => x < 0).length,
    var95: sorted.length ? sorted[Math.floor(sorted.length * 0.05)] : 0,
  };
}

export interface Bucket<K extends string = string> { key: K; s: Summary }

function bucketize<K extends string>(trades: LiteTrade[], order: K[], keyOf: (t: LiteTrade) => K | null): Bucket<K>[] {
  const map = new Map<K, LiteTrade[]>();
  for (const t of closedOnly(trades)) {
    const k = keyOf(t);
    if (k === null) continue;
    map.set(k, [...(map.get(k) ?? []), t]);
  }
  return order.map(key => ({ key, s: summarize(map.get(key) ?? []) })).filter(b => b.s.count > 0);
}

const holdMin = (t: LiteTrade) => (new Date(t.closedAt).getTime() - new Date(t.openedAt).getTime()) / 60_000;

export type HoldKey = "lt2" | "2to5" | "5to15" | "15to30" | "gt30";
export const byHoldTime = (trades: LiteTrade[]) =>
  bucketize<HoldKey>(trades, ["lt2", "2to5", "5to15", "15to30", "gt30"], t => {
    const m = holdMin(t);
    return m < 2 ? "lt2" : m < 5 ? "2to5" : m < 15 ? "5to15" : m < 30 ? "15to30" : "gt30";
  });

export type SizeKey = "1" | "2" | "3" | "4plus";
export const bySize = (trades: LiteTrade[]) =>
  bucketize<SizeKey>(trades, ["1", "2", "3", "4plus"], t => (t.qty >= 4 ? "4plus" : (String(Math.max(1, Math.round(t.qty))) as SizeKey)));

/** Créneaux de la séance US, en heure locale du navigateur (Paris pour la plupart des utilisateurs). */
export type SlotKey = "open" | "morning" | "lunch" | "pm" | "out";
export const byTimeSlot = (trades: LiteTrade[]) =>
  bucketize<SlotKey>(trades, ["open", "morning", "lunch", "pm", "out"], t => {
    const d = new Date(t.openedAt);
    const m = d.getHours() * 60 + d.getMinutes();
    if (m < 930) return "out";
    if (m < 960) return "open";
    if (m < 1050) return "morning";
    if (m < 1140) return "lunch";
    return m < 1320 ? "pm" : "out";
  });

/** Rang du trade dans sa journée : le 1er trade est-il meilleur que les suivants ? */
export type RankKey = "1" | "2" | "3" | "4to5" | "6plus";
export function byTradeIndex(trades: LiteTrade[]) {
  const rank = new Map<string, number>();
  for (const d of byDay(closedOnly(trades))) {
    [...d.trades].sort((a, b) => a.openedAt.localeCompare(b.openedAt)).forEach((t, i) => rank.set(t.id, i + 1));
  }
  return bucketize<RankKey>(trades, ["1", "2", "3", "4to5", "6plus"], t => {
    const r = rank.get(t.id) ?? 1;
    return r >= 6 ? "6plus" : r >= 4 ? "4to5" : (String(r) as RankKey);
  });
}

/** Humeur des sessions de trading (TradingSession.mood), rapportée à la journée de chaque trade. */
export type MoodKey = "excellent" | "good" | "neutral" | "poor" | "bad";
export const byMood = (trades: LiteTrade[], moodByDay: Map<string, MoodKey>) =>
  bucketize<MoodKey>(trades, ["excellent", "good", "neutral", "poor", "bad"], t => moodByDay.get(dayKey(t.openedAt)) ?? null);

export function byMonth(trades: LiteTrade[]) {
  const map = new Map<string, LiteTrade[]>();
  for (const t of closedOnly(trades)) {
    const d = new Date(t.openedAt);
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    map.set(k, [...(map.get(k) ?? []), t]);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([k, ts]) => ({
    key: k,
    date: new Date(`${k}-15T12:00:00`),
    s: summarize(ts),
    days: byDay(ts).length,
  }));
}

export function byTag(trades: LiteTrade[]) {
  const map = new Map<string, { name: string; trades: LiteTrade[] }>();
  for (const t of closedOnly(trades)) {
    for (const tag of t.tags) {
      const e = map.get(tag.id) ?? { name: tag.name, trades: [] };
      e.trades.push(t);
      map.set(tag.id, e);
    }
  }
  return [...map.entries()].map(([id, e]) => ({ key: id, name: e.name, s: summarize(e.trades) })).sort((a, b) => b.s.netPnl - a.s.netPnl);
}

export function byKey(trades: LiteTrade[], keyOf: (t: LiteTrade) => string | null) {
  const map = new Map<string, LiteTrade[]>();
  for (const t of closedOnly(trades)) {
    const k = keyOf(t);
    if (k === null) continue;
    map.set(k, [...(map.get(k) ?? []), t]);
  }
  return [...map.entries()].map(([key, ts]) => ({ key, s: summarize(ts) })).sort((a, b) => b.s.netPnl - a.s.netPnl);
}

/** Taux de réussite glissant sur les N derniers trades. */
export function rollingWinRate(trades: LiteTrade[], locale: string, window = 20) {
  const sorted = closedOnly(trades).sort((a, b) => a.openedAt.localeCompare(b.openedAt));
  const out: { i: number; label: string; value: number }[] = [];
  for (let i = window - 1; i < sorted.length; i++) {
    const slice = sorted.slice(i - window + 1, i + 1);
    out.push({ i, label: new Date(sorted[i].openedAt).toLocaleDateString(locale, { day: "numeric", month: "short" }), value: slice.filter(t => net(t) > 0).length / window });
  }
  return out;
}

/** Chaque trade dans le temps, pour repérer les pertes hors norme. */
export function tradeScatter(trades: LiteTrade[]) {
  return closedOnly(trades).sort((a, b) => a.openedAt.localeCompare(b.openedAt)).map((t, i) => ({ i, pnl: Math.round(net(t)), symbol: t.symbol, date: t.openedAt }));
}

export interface Behavior {
  /** Trades ouverts moins de 10 min après une perte, le même jour. */
  afterLoss: Summary;
  normal: Summary;
  /** Trades pris après avoir atteint l'objectif du jour. */
  afterGoal: Summary;
  /** Trades pris après 2 pertes d'affilée. */
  afterTwoLosses: Summary;
}

export function behavior(trades: LiteTrade[], dailyGoal: number): Behavior {
  const afterLoss: LiteTrade[] = [], normal: LiteTrade[] = [], afterGoal: LiteTrade[] = [], afterTwo: LiteTrade[] = [];
  for (const d of byDay(closedOnly(trades))) {
    const ts = [...d.trades].sort((a, b) => a.openedAt.localeCompare(b.openedAt));
    let cum = 0, losses = 0;
    ts.forEach((t, i) => {
      const prev = ts[i - 1];
      const quick = prev && net(prev) < 0 && new Date(t.openedAt).getTime() - new Date(prev.closedAt).getTime() < 10 * 60_000;
      (quick ? afterLoss : normal).push(t);
      if (cum >= dailyGoal) afterGoal.push(t);
      if (losses >= 2) afterTwo.push(t);
      cum += net(t);
      losses = net(t) < 0 ? losses + 1 : 0;
    });
  }
  return {
    afterLoss: summarize(afterLoss),
    normal: summarize(normal),
    afterGoal: summarize(afterGoal),
    afterTwoLosses: summarize(afterTwo),
  };
}

export function feesStats(trades: LiteTrade[]) {
  const closed = closedOnly(trades);
  const fees = closed.reduce((a, t) => a + t.fees, 0);
  const gross = closed.reduce((a, t) => a + t.pnl, 0);
  return { fees, gross, ratio: gross > 0 ? Math.abs(fees) / gross : 0, perTrade: closed.length ? fees / closed.length : 0 };
}
